import { useId, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { LineChart } from '@Components/Charts/LineChart';
import { TableEmptyState } from '@Components/TableEmptyState';
import { Placement } from '@Components/designConstants';
import { FatigueAdSet, LaunchEvent } from './meta-fatigue-insights-constants';
import {
  buildFatigueChartData,
  emptyStateMessage,
  formatLaunchTooltip,
  launchesInWindow,
  shortDate
} from './meta-fatigue-insights-utils';

interface FatigueTimeChartProps {
  adSets: FatigueAdSet[];
  onSelectLine: (adSetId: string) => void;
  dateRange: DateRange;
  filtersActive: boolean;
}

interface LaunchMarkerDotProps {
  launchesByDate: Map<string, LaunchEvent>;
  adNames: Map<string, string>;
  cx?: number;
  cy?: number;
  stroke?: string;
  payload?: { date?: string };
}

// circle on the line, marking a day new ads went live in that ad set.
export const LaunchMarkerDot = ({
  launchesByDate,
  adNames,
  cx,
  cy,
  stroke,
  payload
}: LaunchMarkerDotProps) => {
  const tooltipId = useId();
  const launch = payload?.date ? launchesByDate.get(payload.date) : undefined;
  if (cx == null || cy == null || !stroke || !launch) {
    return null;
  }
  return (
    <OverlayTrigger
      placement={Placement.TOP}
      overlay={
        <Tooltip id={tooltipId}>
          <div style={{ whiteSpace: 'pre-line' }}>{formatLaunchTooltip(launch, adNames)}</div>
        </Tooltip>
      }
    >
      <g className='fatigue-launch-marker'>
        <circle cx={cx} cy={cy - 5} r={9} fill='transparent' />
        <circle cx={cx} cy={cy} r={4} fill={stroke} stroke='white' strokeWidth={2} />
      </g>
    </OverlayTrigger>
  );
};

// Renders only when no ad set is isolated — CombinedPanel swaps in FocusChart otherwise —
// so there is no dimmed state to express here.
export const FatigueTimeChart = ({
  adSets,
  onSelectLine,
  dateRange,
  filtersActive
}: FatigueTimeChartProps) => {
  const data = useMemo(() => buildFatigueChartData(adSets, dateRange), [adSets, dateRange]);
  const lineDataKeys = useMemo(
    () =>
      adSets.map(adSet => {
        const launches = launchesInWindow(adSet, dateRange);
        if (launches.length === 0) {
          return { dataKey: adSet.adSetId, name: adSet.name };
        }
        const launchesByDate = new Map(launches.map(launch => [launch.date, launch]));
        const adNames = new Map(adSet.ads.map(ad => [ad.adId, ad.name]));
        return {
          dataKey: adSet.adSetId,
          name: adSet.name,
          dot: <LaunchMarkerDot launchesByDate={launchesByDate} adNames={adNames} />
        };
      }),
    [adSets, dateRange]
  );

  if (adSets.length === 0) {
    return (
      <TableEmptyState
        message={emptyStateMessage(
          filtersActive,
          'No ad sets have fatigue history for this account.'
        )}
      />
    );
  }
  if (data.length === 0) {
    return <TableEmptyState message='No fatigue data in the selected date range.' />;
  }

  return (
    <LineChart
      data={data}
      xAxisDataKey='date'
      xAxisTickFormatter={shortDate}
      lineDataKeys={lineDataKeys}
      useLeftYAxis
      customLeftYAxisDomain={['auto', 'auto']}
      yAxisTickFormatter={value => value.toFixed(2)}
      showTooltip={false}
      onLegendClick={line => onSelectLine(line.dataKey)}
    />
  );
};
